import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";

const DESTINATIONS = [
  {
    code: "spain",
    to: "/spain",
    name: "Spain",
    name_es: "España",
    image: "/img/countries/spain.png",
    imageWebp: "/img/countries/spain.webp",
    alt_en: "Andalusian sierra at golden hour",
    alt_es: "Sierra andaluza al atardecer dorado",
    delay: "0s",
    subtitle_en: "Andalucía and the Madrid sierra.",
    subtitle_es: "Andalucía y la sierra madrileña.",
  },
  {
    code: "italy",
    to: "/italy",
    name: "Italy",
    name_es: "Italia",
    image: "/img/countries/italy.png",
    imageWebp: "/img/countries/italy.webp",
    alt_en: "Italian coastal terraces above the Bay of Naples",
    alt_es: "Terrazas costeras italianas sobre el Golfo de Nápoles",
    delay: "-6s",
    subtitle_en: "From the Castelli Romani to the Bay of Naples.",
    subtitle_es: "De los Castelli Romani al Golfo de Nápoles.",
  },
  {
    code: "portugal",
    to: "/portugal",
    name: "Portugal",
    name_es: "Portugal",
    image: "/img/countries/portugal.png",
    imageWebp: "/img/countries/portugal.webp",
    alt_en: "Sintra coastline and Atlantic light",
    alt_es: "Costa de Sintra y luz del Atlántico",
    delay: "-12s",
    subtitle_en: "Lisbon, Sintra and the Atlantic south.",
    subtitle_es: "Lisboa, Sintra y el Atlántico sur.",
  },
];

export default function Destinations() {
  const { lang, t } = useLang();
  return (
    <section data-testid="destinations-section" className="relative bg-charcoal">
      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-24 md:pt-28 pb-10 md:pb-12 text-center text-ivory">
        <p className="eyebrow text-brass">{t.home.destinations_eyebrow}</p>
        <h2
          className="font-display mt-6 leading-[1.05] text-balance"
          style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
        >
          {t.home.destinations_headline_main}{" "}
          <span className="italic-quote font-normal" style={{ fontWeight: 400 }}>
            {t.home.destinations_headline_italic}
          </span>
        </h2>
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-3">
        {DESTINATIONS.map((item) => (
          <Link
            key={item.code}
            to={item.to}
            className="group relative block overflow-hidden h-[55vh] md:h-[70vh]"
            data-testid={`destination-card-${item.code}`}
          >
            <picture>
              <source srcSet={item.imageWebp} type="image/webp" />
              <img
                src={item.image}
                alt={lang === "es" ? item.alt_es : item.alt_en}
                className="absolute inset-0 w-full h-full object-cover dest-kb"
                loading="lazy"
                decoding="async"
                style={{ animationDelay: item.delay }}
              />
            </picture>
            <div
              className="absolute inset-0 transition-opacity duration-700 ease-out group-hover:opacity-90"
              style={{
                background:
                  "linear-gradient(180deg, rgba(15,15,15,0.18) 0%, rgba(15,15,15,0.18) 45%, rgba(15,15,15,0.65) 100%)",
              }}
              aria-hidden
            />
            <div className="relative z-10 h-full flex flex-col justify-end items-start p-8 md:p-12 text-ivory hero-legible">
              <h3
                className="font-display leading-none text-balance"
                style={{ fontSize: "clamp(2.25rem, 4.5vw, 3.25rem)" }}
              >
                {lang === "es" ? item.name_es : item.name}
              </h3>
              <p
                className="italic-quote text-ivory/90 mt-3 text-balance"
                style={{ fontSize: "clamp(1rem, 1.25vw, 1.2rem)" }}
              >
                {lang === "es" ? item.subtitle_es : item.subtitle_en}
              </p>
              <span className="eyebrow text-ivory mt-6 link-underline">{t.cta.discover}</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
